import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, workspaceAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      
      // Load workspace if user has one
      if (response.data.has_workspace) {
        try {
          const wsResponse = await workspaceAPI.get();
          setWorkspace(wsResponse.data);
        } catch (wsErr) {
          console.log('No workspace yet');
        }
      }
    } catch (err) {
      setUser(null);
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Skip auth check for public pages
    const publicPaths = ['/soumission', '/embed', '/page', '/fr/', '/en/', '/login', '/signup', '/forgot-password', '/reset-password', '/mobile'];
    const isPublicPath = publicPaths.some(path => window.location.pathname.startsWith(path));
    if (isPublicPath) {
      setLoading(false);
      return;
    }
    
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('session_token', response.data.session_token);
      
      // Include role, permissions and must_change_password in user state
      const userData = {
        ...response.data,
        role: response.data.role || 'client',
        permissions: response.data.permissions || {},
        must_change_password: response.data.must_change_password || false
      };
      setUser(userData);
      
      // Only load workspace if not required to change password
      if (!userData.must_change_password) {
        try {
          const wsResponse = await workspaceAPI.get();
          setWorkspace(wsResponse.data);
        } catch (wsErr) {
          // No workspace yet
        }
      }
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur de connexion';
      setError(message);
      throw err;
    }
  };

  const register = async (name, email, password) => {
    setError(null);
    try {
      const response = await authAPI.register({ name, email, password });
      localStorage.setItem('session_token', response.data.session_token);
      setUser(response.data);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur d\'inscription';
      setError(message);
      throw new Error(message);
    }
  };

  // Self-serve signup. Mirrors `login`: stores the session token, sets the
  // user (with role/permissions defaults), and loads the workspace that the
  // backend created atomically alongside the user. Returns the API payload
  // so the caller can read `plan_id` / `interval` for post-signup routing.
  const signup = async ({ email, password, name, company_name, plan, primary_domain }) => {
    setError(null);
    try {
      const response = await authAPI.signup({ email, password, name, company_name, plan, primary_domain });
      localStorage.setItem('session_token', response.data.session_token);

      const userData = {
        ...response.data,
        role: response.data.role || 'admin_enterprise',
        permissions: response.data.permissions || {},
        must_change_password: false,
        has_workspace: true,
      };
      setUser(userData);

      try {
        const wsResponse = await workspaceAPI.get();
        setWorkspace(wsResponse.data);
      } catch (wsErr) {
        // Workspace not yet readable; the dashboard will refresh.
      }

      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur d\'inscription';
      setError(message);
      throw err;
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    try {
      const response = await authAPI.exchangeSession(sessionId);
      localStorage.setItem('session_token', response.data.session_token);
      
      // Include role and must_change_password
      const userData = {
        ...response.data,
        role: response.data.role || 'client',
        must_change_password: response.data.must_change_password || false
      };
      setUser(userData);
      
      // Only load workspace if not required to change password
      if (!userData.must_change_password) {
        try {
          const wsResponse = await workspaceAPI.get();
          setWorkspace(wsResponse.data);
        } catch (wsErr) {
          // No workspace yet
        }
      }
      
      return userData;
    } catch (err) {
      console.error('OAuth callback error:', err);
      throw err;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.log('Logout API error (ignored):', err);
    }
    localStorage.removeItem('session_token');
    setUser(null);
    setWorkspace(null);
  };

  const createWorkspace = async (data) => {
    const response = await workspaceAPI.create(data);
    setWorkspace(response.data);
    // Update user to reflect workspace ownership
    setUser(prev => ({ ...prev, has_workspace: true, workspace_id: response.data.workspace_id }));
    return response.data;
  };

  const updateWorkspace = async (data) => {
    const response = await workspaceAPI.update(data);
    setWorkspace(response.data);
    return response.data;
  };

  const refreshWorkspace = async () => {
    try {
      const wsResponse = await workspaceAPI.get();
      setWorkspace(wsResponse.data);
    } catch (err) {
      console.log('Error refreshing workspace:', err);
    }
  };

  const value = {
    user,
    workspace,
    loading,
    error,
    isAuthenticated: !!user,
    hasWorkspace: !!workspace,
    login,
    register,
    signup,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    createWorkspace,
    updateWorkspace,
    refreshWorkspace,
    refreshUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
